import { memo, useLayoutEffect, useMemo, useRef } from 'react'
import { LoadingPlaceholder } from '@sparky/framework'
import { Title, Button } from '@panwds/react-ui'
import { PrimaryButton } from '../common/Common'
import AppLogo from '../common/AppLogo'
import SimpleGallery from './SimpleGallery'
import BodyRenderer from './BodyRenderer'
import ProductReqInfo from './ProductReqInfo'
import './Products.scss'
import type { VFC } from 'react'
import type { SingleInstanceWithApp, AppWithOptionalInstance, AppAccessFilterFn, AppFilters } from '../../types'
import type { Body } from './BodyRenderer'

type ProductsProps = Partial<SingleInstanceWithApp> & AppFilters

const AppDetails: VFC<AppWithOptionalInstance & { appAccessFilterFn: AppAccessFilterFn }> = ({ instance, app, appAccessFilterFn }) => {
  const primaryBtn = appAccessFilterFn(app) && <PrimaryButton instance={instance} app={app} />
  const logo = <AppLogo src={app.logo || app.normalized_logo_url} alt={' '} />
  const elRef = useRef<HTMLDivElement>(null)
  const {
    summary = app.description || null,
    product_link = null,
    heading,
    gallery_image_urls,
    body,
    system_requirements,
    categories,
    first_release,
    latest_release,
    tac_link,
    privacy_link,
    contact_link,
  } = app.marketplace_content || {}
  const appReqDevInfo = {
    ...app.developer,
    system_requirements,
    first_release,
    latest_release,
    categories,
    tac_link,
    privacy_link,
    product_link,
    contact_link,
  }

  useLayoutEffect(() => {
    elRef.current?.closest('.sparky-hub')?.scrollTo?.(0, 0)
  })

  return <div ref={elRef} data-testid={`sparky-hub-app-details:${app.app_id}`}>
    <div className='sparky-hub-app-header tw-dark'>
      {logo}
      <Title level={1} addClassName='cm-text-white'>{app.display_name}</Title>
      <div className='sparky-hub-app-summery'>{summary}</div>
      <div className='sparky-hub-app-actions'>
        {primaryBtn}
        {product_link && <a
          href={product_link}
          rel='external noopener noreferrer'
          target='_blank'
        ><Button appearance='clear' size='md'>Learn More</Button></a>}
      </div>
    </div>
    <div className='sparky-hub-app-content'>
      {Boolean(heading && heading !== app.display_name) && <Title level={2} addClassName='sparky-hub-app-heading'>{heading}</Title>}
      <div className='cm-flex cm-items-start'>
        <div className='cm-flex-grow cm-items-start'>
          {gallery_image_urls && <SimpleGallery images={gallery_image_urls} />}
          <BodyRenderer body={body as Body} />
        </div>
        <ProductReqInfo {...appReqDevInfo} />
      </div>
    </div>
  </div>
}

const MemoAppDetails = memo(AppDetails)

const Products: VFC<ProductsProps> = ({ instance, app, appFilterFn, appAccessFilterFn }) => {
  const invalid = !(app && appFilterFn(app))
  const redirectUrl = useMemo((): string => {
    if (invalid || !app.marketplace_content) {
      return ''
    }
    const { url, product_link, body } = app.marketplace_content
    if (url) {
      return url as string
    }
    if (product_link && !body) {
      return product_link
    }
    return ''
  }, [app, invalid])

  useLayoutEffect(() => {
    if (redirectUrl) {
      window.location.href = redirectUrl
    }
  }, [redirectUrl])

  if (invalid) {
    return <>404 Not Found</>
  }
  else if (redirectUrl) {
    return <LoadingPlaceholder />
  }

  return <MemoAppDetails app={app} instance={instance} appAccessFilterFn={appAccessFilterFn} />
}

export default Products
